import { Mic, MicOff, Radio } from "lucide-react";
import type { StudioController } from "../useStudio";

/** Capture state belongs beside the monitor, including its recovery controls. */
export function ListeningIndicator({ studio }: { studio: StudioController }) {
  const speech = studio.speechStatus;
  const external = speech.active && speech.engine === "external";
  const active = speech.active && !external;
  const mode = studio.state.settings.mode;
  const available = mode === "voice" || mode === "sound";
  const state = speech.preparing
    ? "preparing"
    : speech.error
      ? "error"
      : external
        ? "external"
        : active
          ? "active"
          : "idle";
  const busy = studio.modelStatus.busy || studio.audioStatus.busy;
  const label = speech.preparing
    ? "Cancel speech preparation"
    : active
      ? "Stop listening"
      : "Retry listening";
  const help = speech.error
    ? `${speech.error} ${busy ? "Finish or cancel the audio task in Tools before retrying." : "Click to retry listening. Manual controls remain available."}`
    : speech.preparing
      ? `${speech.status}. Click to cancel before listening starts.`
      : active
        ? `${speech.status}. Click to stop microphone listening. Pausing a take stops scrolling but keeps listening on.`
        : external
          ? "External transcript input is ready. This does not use your microphone. Send text from Settings & tools → Speech setup."
          : available
            ? busy
              ? "Finish or cancel model preparation or audio transcription in Tools before retrying listening."
              : "Microphone is off. Click to retry listening for the selected scroll mode."
            : "Microphone is off. Selecting Voice tracking or Sound gated starts listening. The browser may ask for microphone permission.";
  const text = speech.preparing
    ? speech.progress && speech.progress < 100
      ? `Preparing ${Math.round(speech.progress)}%`
      : "Starting…"
    : active
      ? "Listening"
      : external
        ? "Transcript ready"
        : speech.error
          ? "Mic unavailable"
          : "Mic off";
  const content = (
    <>
      <span className="microphone-pulse" aria-hidden="true">
        {external ? <Radio size={13} /> : active || speech.preparing ? <Mic size={13} /> : <MicOff size={13} />}
      </span>
      <span>{text}</span>
    </>
  );
  return (
    <>
      {available && !external ? (
        <button
          type="button"
          className="microphone-indicator"
          data-state={state}
          data-help={help}
          aria-label={label}
          disabled={!active && !speech.preparing && busy}
          onClick={() => {
            if (active || speech.preparing) studio.stopSpeech();
            else void studio.startSpeech();
          }}
        >{content}</button>
      ) : (
        <span className="microphone-indicator" data-state={state} data-help={help} tabIndex={0}>
          {content}
        </span>
      )}
      <span className="visually-hidden" role="status">{speech.error ? `Microphone unavailable: ${speech.error}` : external ? "External transcript ready; microphone off" : speech.preparing ? speech.status : active ? "Microphone listening" : "Microphone off"}</span>
    </>
  );
}
